import React, { useEffect, useState, useRef } from "react"

const DAYS = [
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
  { key: "sunday", label: "Sun" }
]

const DEFAULT_START = "08:00"
const DEFAULT_FINISH = "17:00"

const getTimeValue = value => {
  return value || ""
}

const formatTo12Hour = (time24) => {
  if (!time24 || time24 === "") return ""
  
  const [hours, minutes] = time24.split(":").map(Number)
  if (isNaN(hours) || isNaN(minutes)) return time24
  
  const period = hours >= 12 ? "PM" : "AM"
  const hours12 = hours === 0 ? 12 : (hours > 12 ? hours - 12 : hours)
  const minutesStr = minutes.toString().padStart(2, "0")
  
  return `${hours12}:${minutesStr} ${period}`
}

const buildState = (source, applyDefaults = true) =>
  DAYS.reduce((acc, { key }) => {
    const dayData = source?.[key] || {}
    const startValue = getTimeValue(dayData.start)
    const finishValue = getTimeValue(dayData.finish)
    acc[key] = {
      available: !!dayData.available,
      start: applyDefaults 
        ? (startValue || DEFAULT_START)
        : (startValue || ""),
      finish: applyDefaults
        ? (finishValue || DEFAULT_FINISH)
        : (finishValue || "")
    }
    return acc
  }, {})

const buildOriginalState = source =>
  DAYS.reduce((acc, { key }) => {
    const dayData = source?.[key] || {}
    acc[key] = {
      available: !!dayData.available,
      start: dayData.start || null,
      finish: dayData.finish || null
    }
    return acc
  }, {})

const UserAvailabitlityDays = ({
  editState,
  title,
  availability,
  editableAvailability,
  updateAttr
}) => {
  const [localAvailability, setLocalAvailability] = useState(
    buildState(editState ? editableAvailability : availability)
  )
  const [originalAvailability, setOriginalAvailability] = useState(
    buildOriginalState(availability)
  )
  const [validationErrors, setValidationErrors] = useState({})

  useEffect(() => {
    const serverState = buildOriginalState(availability)
    setOriginalAvailability(serverState)
  }, [availability])

  const prevEditStateRef = useRef(editState)

  useEffect(() => {
    const enteringEditMode = editState && !prevEditStateRef.current
    const exitingEditMode = !editState && prevEditStateRef.current
    
    if (enteringEditMode) {
      setLocalAvailability(buildState(editableAvailability))
    } else if (exitingEditMode) {
      setLocalAvailability(buildState(availability))
    } else if (!editState) {
      setLocalAvailability(buildState(availability))
    }
    
    prevEditStateRef.current = editState
  }, [editState, availability, editableAvailability])

  const getValueToSend = (dayData, field, originalValue = null) => {
    if (!dayData.available) return null
    const fieldValue = dayData[field]
    const defaultValue = field === "start" ? DEFAULT_START : DEFAULT_FINISH
    
    if (!fieldValue || fieldValue === "") {
      return null
    }
    
    if (fieldValue === defaultValue && originalValue === null) {
      return null
    }
    
    return fieldValue
  }

  const hasDayChanged = (dayKey, dayData) => {
    const original = originalAvailability[dayKey]
    if (!original) {
      return dayData.available && (dayData.start || dayData.finish)
    }

    if (original.available !== dayData.available) return true

    if (!original.available && !dayData.available) return false

    if (!original.available && dayData.available) return true

    if (original.available && !dayData.available) return true

    const getCompareValue = (time) => {
      if (!time || time === "") return null
      return time
    }

    const originalStart = getCompareValue(original.start)
    const originalFinish = getCompareValue(original.finish)
    
    const valueToSendStart = getValueToSend(dayData, "start", original.start)
    const valueToSendFinish = getValueToSend(dayData, "finish", original.finish)
    
    const currentStart = getCompareValue(valueToSendStart)
    const currentFinish = getCompareValue(valueToSendFinish)

    const startChanged = originalStart !== currentStart
    const finishChanged = originalFinish !== currentFinish
    
    return startChanged || finishChanged
  }

  const persistDay = (dayKey, dayData) => {
    if (!hasDayChanged(dayKey, dayData)) {
      return
    }

    const original = originalAvailability[dayKey]
    const startValue = getValueToSend(dayData, "start", original?.start)
    const finishValue = getValueToSend(dayData, "finish", original?.finish)

    updateAttr({
      [dayKey]: dayData.available,
      [`${dayKey}_start_time`]: startValue,
      [`${dayKey}_finish_time`]: finishValue
    })
  }

  const handleToggle = (dayKey, checked) => {
    setLocalAvailability(prev => {
      const updatedDay = {
        ...prev[dayKey],
        available: checked,
        start: checked ? (prev[dayKey]?.start || DEFAULT_START) : "",
        finish: checked ? (prev[dayKey]?.finish || DEFAULT_FINISH) : ""
      }
      const updated = {
        ...prev,
        [dayKey]: updatedDay
      }
      persistDay(dayKey, updatedDay)
      return updated
    })
  }

  const compareTimes = (time1, time2) => {
    if (!time1 || !time2) return 0
    const [hours1, minutes1] = time1.split(":").map(Number)
    const [hours2, minutes2] = time2.split(":").map(Number)
    const totalMinutes1 = hours1 * 60 + minutes1
    const totalMinutes2 = hours2 * 60 + minutes2
    return totalMinutes1 - totalMinutes2
  }

  const handleTimeChange = (dayKey, field, value) => {
    setLocalAvailability(prev => {
      const currentDay = prev[dayKey]
      const otherField = field === "start" ? "finish" : "start"
      const otherValue = currentDay?.[otherField] || ""

      setValidationErrors(prevErrors => {
        const newErrors = { ...prevErrors }
        delete newErrors[`${dayKey}_${field}`]
        return newErrors
      })

      if (value && otherValue) {
        if (field === "start" && compareTimes(value, otherValue) > 0) {
          setValidationErrors(prevErrors => ({
            ...prevErrors,
            [`${dayKey}_${field}`]: "Start time must not be greater than finish time"
          }))
          return prev
        }
        if (field === "finish" && compareTimes(otherValue, value) > 0) {
          setValidationErrors(prevErrors => ({
            ...prevErrors,
            [`${dayKey}_${field}`]: "Finish time must not be less than start time"
          }))
          return prev
        }
      }

      const updated = {
        ...prev,
        [dayKey]: {
          ...prev[dayKey],
          [field]: value
        }
      }
      persistDay(dayKey, updated[dayKey])
      return updated
    })
  }

  const renderTableRows = ({ readonly }) => (
    <tbody>
      {DAYS.map(({ key, label }) => {
        const dayData = localAvailability[key]
        const available = dayData?.available
        const startValue = dayData?.start || ""
        const finishValue = dayData?.finish || ""

        return (
          <tr key={key}>
            <td className="align-middle text-center" style={{ width: "120px" }}>
              {readonly ? (
                <span className={`badge ${available ? "bg-success" : "bg-secondary"}`}>
                  {available ? "Yes" : "No"}
                </span>
              ) : (
                <div className="custom-control custom-checkbox d-inline-block">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id={`${key}-available`}
                    checked={available}
                    onChange={event => handleToggle(key, event.target.checked)} />
                  <label className="custom-control-label" htmlFor={`${key}-available`} />
                </div>
              )}
            </td>
            <td className="align-middle" style={{ width: "120px" }}>{label}</td>
            <td className="align-middle" style={{ width: "160px" }}>
              {readonly ? (
                available ? formatTo12Hour(startValue || DEFAULT_START) : "-"
              ) : (
                <div>
                  <input
                    type="time"
                    className={`form-control ${validationErrors[`${key}_start`] ? "is-invalid" : ""}`}
                    value={startValue}
                    disabled={!available}
                    onChange={event => handleTimeChange(key, "start", event.target.value)} />
                  {validationErrors[`${key}_start`] && (
                    <div className="invalid-feedback d-block" style={{ fontSize: "0.75rem" }}>
                      {validationErrors[`${key}_start`]}
                    </div>
                  )}
                </div>
              )}
            </td>
            <td className="align-middle" style={{ width: "160px" }}>
              {readonly ? (
                available ? formatTo12Hour(finishValue || DEFAULT_FINISH) : "-"
              ) : (
                <div>
                  <input
                    type="time"
                    className={`form-control ${validationErrors[`${key}_finish`] ? "is-invalid" : ""}`}
                    value={finishValue}
                    disabled={!available}
                    onChange={event => handleTimeChange(key, "finish", event.target.value)} />
                  {validationErrors[`${key}_finish`] && (
                    <div className="invalid-feedback d-block" style={{ fontSize: "0.75rem" }}>
                      {validationErrors[`${key}_finish`]}
                    </div>
                  )}
                </div>
              )}
            </td>
          </tr>
        )
      })}
    </tbody>
  )

  return (
    <div className={`row ${editState ? "mb-4 mt-5" : "mb-2"}`}>
      <style>{`
        .user-availability-table thead th {
          border-top: none !important;
          padding-bottom: 1rem;
        }
      `}</style>
      <div className="col-12">
        <div className="d-flex">
          <div className="col-3 pl-0 pr-0">
            <strong>{title}</strong>
          </div>
          <div className="col-9 pl-0 pr-0">
            <table className="table table-sm mb-0 user-availability-table">
              <thead>
                <tr>
                  <th style={{ width: "120px" }}>Available</th>
                  <th style={{ width: "120px" }}>Days</th>
                  <th style={{ width: "160px" }}>Start</th>
                  <th style={{ width: "160px" }}>Finish</th>
                </tr>
              </thead>
              {renderTableRows({ readonly: !editState })}
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserAvailabitlityDays
